import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { userService } from 'app/services/user.service';
import { useSessionSecurity } from 'app/auth/useSessionSecurity';
import StepUpIntroDialog from 'app/auth/StepUpIntroDialog';
import StepUpNoPhoneDialog from 'app/auth/StepUpNoPhoneDialog';
import StepUpOtpModal from 'app/auth/StepUpOtpModal';

export interface UseBankLinkStepUpGateResult {
  /**
   * Run `proceed` (e.g. openPlaidLink) immediately if the session is already
   * MFA; otherwise open the SMS step-up modal and only run it after a verified
   * code. Use this to wrap every "connect bank account" entry point.
   */
  guard: (proceed: () => void) => void;
  /** Render this once in the consuming component. */
  modal: JSX.Element;
}

export interface StepUpGateCopy {
  /** Intro dialog supporting text (defaults to the bank-link wording). */
  introBody?: string;
  /** No-phone dialog supporting text (defaults to the bank-link wording). */
  noPhoneBody?: string;
}

/**
 * SEE-221: reusable bank-link step-up gate. Single source of the OTP modal so
 * credit auto-pay, debit funding, and onboarding all gate identically. The
 * request/verify calls hit the v2Security resolver on the CDK AppSync API via
 * userService. Flows that reuse the gate for a different action (e.g. passkey
 * creation, SEE-213) pass their own dialog copy so the modals never promise
 * the wrong follow-up.
 */
export function useBankLinkStepUpGate(copy?: StepUpGateCopy): UseBankLinkStepUpGateResult {
  const { isBankLinkStepUpRequiredNow, recordStepUpVerified } = useSessionSecurity();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'intro' | 'code' | 'no-phone'>('intro');
  const [maskedPhone, setMaskedPhone] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const proceedRef = useRef<(() => void) | null>(null);

  const sendOtp = useCallback(async (): Promise<'sent' | 'no_phone' | 'error'> => {
    setLoading(true);
    setError(null);
    setAttemptsRemaining(null); // a fresh code resets the attempt cap
    try {
      const challenge = await userService.requestBankLinkStepUp();
      if (!challenge.sent) return 'no_phone'; // no mobile number on file
      setMaskedPhone(challenge.maskedPhone ?? undefined);
      return 'sent';
    } catch (e) {
      captureException(e);
      setError('We could not send a code. Please try again.');
      return 'error';
    } finally {
      setLoading(false);
    }
  }, []);

  const guard = useCallback(
    (proceed: () => void) => {
      if (!isBankLinkStepUpRequiredNow()) {
        proceed();
        return;
      }
      // Open the "Verify account" confirmation first — no SMS is sent until the
      // user clicks "Send code".
      proceedRef.current = proceed;
      setError(null);
      setAttemptsRemaining(null);
      setMaskedPhone(undefined);
      setStep('intro');
      setOpen(true);
    },
    [isBankLinkStepUpRequiredNow]
  );

  const handleSend = useCallback(async () => {
    const result = await sendOtp();
    // No phone on file → route to the "add a phone" dialog; otherwise show the
    // code screen (a transient send error shows there too, with resend).
    setStep(result === 'no_phone' ? 'no-phone' : 'code');
  }, [sendOtp]);

  // Resend from the code screen — if the phone vanished meanwhile, fall back to
  // the no-phone dialog.
  const handleResend = useCallback(async () => {
    const result = await sendOtp();
    if (result === 'no_phone') setStep('no-phone');
  }, [sendOtp]);

  const goToProfile = useCallback(() => {
    setOpen(false);
    setStep('intro');
    proceedRef.current = null;
    navigate('/profile/settings');
  }, [navigate]);

  const onSubmit = useCallback(
    async (code: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await userService.verifyBankLinkStepUp(code);
        if (result.verified) {
          // Persist the signed grant so the Plaid link-token request can prove
          // the step-up server-side (SEE-221).
          recordStepUpVerified(result.grant ?? undefined);
          setOpen(false);
          const proceed = proceedRef.current;
          proceedRef.current = null;
          proceed?.();
          return;
        }
        // Wrong code: surface the remaining-attempts count (shown under the
        // boxes), which also clears them for re-entry. Fall back to a hard
        // error only when the backend didn't return a count.
        const left = result.attemptsRemaining;
        if (typeof left === 'number') {
          setAttemptsRemaining(left);
          setError(null);
        } else {
          setError('Incorrect code. Please try again.');
        }
      } catch (e) {
        captureException(e);
        setError('Verification failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [recordStepUpVerified]
  );

  const onClose = useCallback(() => {
    setOpen(false);
    setStep('intro');
    setError(null);
    setAttemptsRemaining(null);
    proceedRef.current = null;
  }, []);

  const modal = (
    <>
      <StepUpIntroDialog
        open={open && step === 'intro'}
        loading={loading}
        body={copy?.introBody}
        onSend={handleSend}
        onClose={onClose}
      />
      <StepUpOtpModal
        open={open && step === 'code'}
        maskedPhone={maskedPhone}
        loading={loading}
        error={error}
        attemptsRemaining={attemptsRemaining}
        onSubmit={onSubmit}
        onResend={handleResend}
        onClose={onClose}
      />
      <StepUpNoPhoneDialog
        open={open && step === 'no-phone'}
        body={copy?.noPhoneBody}
        onGoToProfile={goToProfile}
        onClose={onClose}
      />
    </>
  );

  return { guard, modal };
}

export default useBankLinkStepUpGate;
